// SPDX-License-Identifier: Elastic-2.0
/**
 * useFocusTrap — keep keyboard focus inside a container while it's mounted.
 *
 * Drop-in hook for Modal / Drawer / Dialog primitives. While `active` is
 * true:
 *  - Focus is moved into the container on mount (first tabbable, or the
 *    container itself if nothing tabbable exists).
 *  - Tab + Shift-Tab cycle within the container; reaching the last/first
 *    element wraps to the other end (per WAI-ARIA APG "modal dialog").
 *  - On deactivation/unmount, focus is restored to whatever was focused
 *    before activation (typically the trigger button).
 *
 * The hook does NOT handle Esc-to-close — that's the consumer's job
 * (Modal binds it directly). Keeping concerns separate so non-modal
 * surfaces (e.g. a future drawer) can reuse the trap.
 *
 * Returns a ref to attach to the trap container.
 */
import { useEffect, useRef } from "react";

/**
 * CSS selector for elements considered tabbable. This intentionally
 * matches the WAI-ARIA APG recommendation, not the looser CSS
 * `:focusable` definition — we don't want to land on `tabindex="-1"`
 * helpers.
 */
const TABBABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function getTabbables(container: HTMLElement): HTMLElement[] {
  const nodes = Array.from(container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR));
  return nodes.filter((node) => {
    // Skip elements that are visually hidden or inert.
    if (node.hasAttribute("disabled")) return false;
    if (node.getAttribute("aria-hidden") === "true") return false;
    // Skip elements inside `inert` ancestors.
    let cursor: HTMLElement | null = node;
    while (cursor) {
      if (cursor.hasAttribute("inert")) return false;
      cursor = cursor.parentElement;
    }
    return true;
  });
}

export function useFocusTrap<T extends HTMLElement = HTMLElement>(active: boolean) {
  const containerRef = useRef<T | null>(null);

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    // Remember whoever had focus before us, so we can restore on close.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Move focus into the trap. If nothing tabbable lives inside, give
    // the container itself a tabindex of -1 and focus it so subsequent
    // Tab presses land on the first tabbable.
    const tabbables = getTabbables(container);
    if (tabbables.length > 0) {
      tabbables[0]?.focus();
    } else {
      if (!container.hasAttribute("tabindex")) {
        container.setAttribute("tabindex", "-1");
      }
      container.focus();
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== "Tab") return;
      if (!containerRef.current) return;
      const tabbablesNow = getTabbables(containerRef.current);
      if (tabbablesNow.length === 0) {
        event.preventDefault();
        containerRef.current.focus();
        return;
      }
      const first = tabbablesNow[0]!;
      const last = tabbablesNow[tabbablesNow.length - 1]!;
      const current = document.activeElement as HTMLElement | null;

      if (event.shiftKey) {
        if (current === first || !containerRef.current.contains(current)) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (current === last || !containerRef.current.contains(current)) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      // Restore focus only if the previously-focused element is still
      // connected; if it was unmounted, leave focus alone.
      if (previouslyFocused && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [active]);

  return containerRef;
}
